import { useEffect, useState } from "react";
import { Coins, TrendingUp, AlertCircle, Loader2, Settings } from "lucide-react";
import { toast } from "sonner";
import client from "@/lib/api";

/**
 * <DFSCostWidget />
 * Live DataForSEO spend estimate + monthly budget alert configurator.
 * Endpoint is gated to admins + agency tier; this component silently
 * hides itself for other tiers.
 *
 * Props:
 *   projectId  (optional) — if set, shows PER-PROJECT spend instead of account-wide.
 */
export function DFSCostWidget({ projectId = null }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [visible, setVisible] = useState(true);
    const [editingBudget, setEditingBudget] = useState(false);
    const [budgetInput, setBudgetInput] = useState("");
    const [ccEnabled, setCcEnabled] = useState(false);
    const [ccEmailsInput, setCcEmailsInput] = useState("");
    const [saving, setSaving] = useState(false);

    const spendUrl = projectId ? `/projects/${projectId}/dfs-spend` : "/dfs-spend/me";
    const budgetUrl = projectId ? `/projects/${projectId}/dfs-spend/budget` : "/dfs-spend/budget";
    const scopeLabel = projectId ? "PROJECT DFS SPEND" : "DFS SPEND";

    const load = () => {
        setLoading(true);
        client.get(spendUrl)
            .then((r) => {
                setData(r.data);
                setBudgetInput(String(r.data.budget_usd || ""));
                setCcEnabled(Boolean(r.data.client_cc_enabled));
                setCcEmailsInput((r.data.client_emails || []).join(", "));
            })
            .catch((err) => {
                if (err?.response?.status === 403) setVisible(false);
            })
            .finally(() => setLoading(false));
    };

    useEffect(load, [projectId]);

    const saveBudget = async () => {
        setSaving(true);
        try {
            const val = parseFloat(budgetInput) || 0;
            const payload = { budget_usd: val };
            if (projectId) {
                const emails = ccEmailsInput
                    .split(/[,\s]+/)
                    .map((s) => s.trim())
                    .filter(Boolean);
                payload.client_emails = emails;
                payload.client_cc_enabled = ccEnabled && emails.length > 0;
            }
            await client.post(budgetUrl, payload);
            const ccNote = projectId && payload.client_cc_enabled
                ? ` · CC ${payload.client_emails.length} client${payload.client_emails.length === 1 ? "" : "s"}`
                : "";
            toast.success(val > 0 ? `Budget alert set at $${val.toFixed(2)}/mo${ccNote}` : "Budget alert disabled");
            setEditingBudget(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save budget");
        } finally {
            setSaving(false);
        }
    };

    if (!visible) return null;
    if (loading || !data) {
        return (
            <div data-testid="dfs-cost-widget-loading" className="border border-border p-6 bg-card/40">
                <div className="overline flex items-center gap-2"><Coins className="w-3 h-3" /> DFS SPEND</div>
                <div className="mt-4 flex items-center gap-2 text-muted-foreground text-sm">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading…
                </div>
            </div>
        );
    }

    const usd = data.usd || 0;
    const projected = data.projected_month_end_usd || 0;
    const budget = data.budget_usd || 0;
    const pct = data.budget_pct_used;
    const over80 = pct !== null && pct >= 80;
    const overBudget = pct !== null && pct >= 100;
    const barColor = overBudget ? "bg-destructive" : over80 ? "bg-warning" : "bg-primary";

    return (
        <div
            data-testid="dfs-cost-widget"
            className="border border-border p-6 bg-card/40 hover:border-primary/40 transition-colors"
        >
            <div className="flex items-start justify-between">
                <div>
                    <div className="overline flex items-center gap-2"><Coins className="w-3 h-3" /> {scopeLabel} · {data.period}</div>
                    <div className="mt-3 flex items-baseline gap-2">
                        <span data-testid="dfs-spend-usd" className="font-mono text-3xl">${usd.toFixed(2)}</span>
                        <span className="text-xs text-muted-foreground">month-to-date</span>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground font-mono flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" />
                        <span data-testid="dfs-spend-projected">Projected month-end: ${projected.toFixed(2)}</span>
                    </div>
                </div>
                <button
                    data-testid="dfs-budget-edit-btn"
                    onClick={() => setEditingBudget((v) => !v)}
                    className="text-muted-foreground hover:text-foreground p-1"
                    aria-label="Edit budget"
                    title="Set monthly budget alert"
                >
                    <Settings className="w-4 h-4" />
                </button>
            </div>

            {/* Budget bar */}
            {budget > 0 && (
                <div className="mt-4">
                    <div className="flex items-center justify-between text-[10px] font-mono text-muted-foreground">
                        <span>Budget: <span data-testid="dfs-budget-amount" className="text-foreground">${budget.toFixed(2)}</span></span>
                        <span data-testid="dfs-budget-pct" className={overBudget ? "text-destructive" : over80 ? "text-warning" : ""}>{pct}%</span>
                    </div>
                    <div className="mt-1 h-1.5 bg-border/40 overflow-hidden">
                        <div className={`h-full ${barColor} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                    {over80 && (
                        <div data-testid="dfs-over-budget-warning" className="mt-2 text-[10px] font-mono flex items-center gap-1 text-warning">
                            <AlertCircle className="w-3 h-3" />
                            {overBudget ? "Over budget" : "Approaching budget · email alert scheduled"}
                        </div>
                    )}
                </div>
            )}

            {/* Budget editor */}
            {editingBudget && (
                <div data-testid="dfs-budget-editor" className="mt-4 pt-4 border-t border-border space-y-2">
                    <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">Monthly budget alert (USD)</div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-mono text-muted-foreground">$</span>
                        <input
                            data-testid="dfs-budget-input"
                            type="number"
                            min="0"
                            step="10"
                            value={budgetInput}
                            onChange={(e) => setBudgetInput(e.target.value)}
                            placeholder="0 = disabled"
                            className="flex-1 bg-input border border-border px-2 py-1 text-sm font-mono focus:outline-none focus:border-primary"
                        />
                        <button
                            data-testid="dfs-budget-save"
                            onClick={saveBudget}
                            disabled={saving}
                            className="bg-primary text-primary-foreground px-3 py-1 text-xs font-mono hover:bg-primary/90 disabled:opacity-50"
                        >
                            {saving ? "…" : "Save"}
                        </button>
                    </div>
                    <div className="text-[10px] font-mono text-muted-foreground">
                        Email alert fires at 80% of budget · once per month
                    </div>

                    {/* Client CC — per-project only */}
                    {projectId && (
                        <div data-testid="dfs-client-cc-block" className="pt-3 mt-2 border-t border-border/60 space-y-2">
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                    data-testid="dfs-client-cc-toggle"
                                    type="checkbox"
                                    checked={ccEnabled}
                                    onChange={(e) => setCcEnabled(e.target.checked)}
                                    className="accent-primary"
                                />
                                <span className="text-[11px] font-mono text-foreground">
                                    CC client on 80% budget alert
                                </span>
                            </label>
                            <input
                                data-testid="dfs-client-cc-emails"
                                type="text"
                                value={ccEmailsInput}
                                onChange={(e) => setCcEmailsInput(e.target.value)}
                                placeholder="client@example.com, cfo@example.com"
                                disabled={!ccEnabled}
                                className="w-full bg-input border border-border px-2 py-1 text-xs font-mono focus:outline-none focus:border-primary disabled:opacity-40"
                            />
                            <div className="text-[10px] font-mono text-muted-foreground">
                                Up to 5 client emails · comma-separated · scope creep, visible from day one
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Breakdown */}
            {data.by_action && Object.keys(data.by_action).length > 0 && (
                <details data-testid="dfs-breakdown" className="mt-4 pt-4 border-t border-border">
                    <summary className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground">
                        Breakdown by action
                    </summary>
                    <div className="mt-2 space-y-1">
                        {Object.entries(data.by_action).map(([act, b]) => (
                            <div key={act} className="flex items-center justify-between text-[11px] font-mono">
                                <span className="text-muted-foreground">{act.replace(/_/g, " ")}</span>
                                <span>
                                    <span className="text-foreground">{b.credits} cr</span>
                                    <span className="text-muted-foreground ml-2">${b.usd.toFixed(3)}</span>
                                </span>
                            </div>
                        ))}
                    </div>
                </details>
            )}
        </div>
    );
}
